import { Driver } from '@/services/api/drivers';

/**
 * Scheduled Instant rider-picker tag names.
 * DEDICATED is filtered server-side (v3 `tagNames`); EXPRESS is hidden
 * client-side on the "All Riders" tab (driver-service has no exclude-tag
 * filter yet — see TODO T-DRV). This module isolates that client-side logic
 * so the count/paging footgun is unit-testable.
 */
export const SI_TAG = {
  // Exact driver-service tag name — must match verbatim ("Schedule Instant",
  // not "Scheduled"). The server-side tagNames filter is case-sensitive on the
  // driver-service side, so a mismatch silently returns zero dedicated riders.
  DEDICATED: 'Schedule Instant',
  EXPRESS: 'Express',
} as const;

/** Case-insensitive check for whether a driver carries a given tag name. */
export function driverHasTag(driver: Driver, tagName: string): boolean {
  const target = tagName.trim().toLowerCase();
  return (driver.tags ?? []).some((t) => {
    const name = typeof t === 'string' ? t : t?.name;
    return typeof name === 'string' && name.trim().toLowerCase() === target;
  });
}

export interface ExpressFilterResult {
  visible: Driver[];
  /** How many drivers were dropped for carrying the excluded tag. */
  hiddenCount: number;
}

/**
 * Drop drivers carrying `excludeTagName` (default Express) from a page.
 * Returns the visible subset + how many were hidden, so the UI can show a
 * "+N disembunyikan" caveat and callers can decide to fetch more when a page
 * comes back mostly/all hidden (the pagination footgun this guards).
 */
export function filterExpressRiders(
  drivers: Driver[],
  excludeTagName: string = SI_TAG.EXPRESS
): ExpressFilterResult {
  const visible = drivers.filter((d) => !driverHasTag(d, excludeTagName));
  return { visible, hiddenCount: drivers.length - visible.length };
}
